<template>
    <div>
        <transition
            v-if="showBanner"
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-out"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
        >
            <div class="fixed bottom-0 left-0 z-50 flex items-end justify-center w-full h-auto p-2 bg-gradient-to-t from-black md:pb-10">
                <div class="flex items-center max-w-screen-lg gap-16 px-8 py-8 bg-gray-700 shadow-xl md:px-16 rounded border border-gray-500">
                    <div>
                        <p class="text-xl font-bold text-center text-white md:text-left">
                            Can you spare a cookie?
                        </p>
                        <p class="mt-2 text-center text-gray-300 md:text-left">
                            We use cookies to help improve the site functionality and optimize your experience.
                        </p>
                        <div class="flex flex-col gap-2 mt-5 md:flex-row md:gap-4">
                            <BaseButton @click="acceptAllClicked">
                                Accept All
                            </BaseButton>
                            <BaseButton
                                variant="text"
                                @click="showSettingsClicked"
                            >
                                Manage
                            </BaseButton>
                            <BaseButton
                                to="/privacy-policy"
                                variant="text"
                            >
                                Privacy Policy
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <div
            v-if="showSettings"
            class="fixed bottom-0 left-0 z-50 flex items-center justify-center w-full h-screen p-2 md:p-10 backdrop-brightness-50"
        >
            <div class="flex flex-col items-center max-w-screen-md max-h-full gap-0 px-4 py-4 bg-gray-700 shadow-xl md:py-8 md:px-16 rounded border border-gray-500">
                <div class="flex w-full gap-4 md:gap-8">
                    <ShieldCheckIcon
                        aria-hidden="true"
                        class="inline-block -mt-3 text-white w-14 h-14"
                    />
                    <div>
                        <p class="text-xl font-bold text-white">
                            Privacy Settings
                        </p>
                        <p class="mt-2 mb-5 text-xs text-gray-300 md:text-sm">
                            We use cookies to help improve the site functionality and optimize your experience. You can
                            manage your cookie settings here.
                        </p>
                    </div>
                    <XIcon
                        v-if="acceptedAllCookies"
                        aria-hidden="true"
                        class="inline-block w-9 h-9 p-2 mr-0.5 text-white rounded hover:bg-gray-600 hover:cursor-pointer flex-shrink-0"
                        @click="closeSettingsClicked"
                    />
                </div>
                <div class="w-full overflow-y-auto">
                    <div
                        v-for="item in cookieSettingsData"
                        :key="item.id"
                    >
                        <Disclosure v-slot="{ open }">
                            <DisclosureButton class="flex justify-between w-full px-2 py-3 text-sm font-medium text-white border-t border-gray-600 rounded hover:bg-gray-600">
                                <span>{{ item.title }}</span>
                                <ChevronRightIcon
                                    :class="open ? 'transform -rotate-90' : 'transform rotate-90'"
                                    class="inline w-5 h-5"
                                />
                            </DisclosureButton>
                            <transition
                                enter-active-class="transition duration-100 ease-out"
                                enter-from-class="transform scale-95 opacity-0"
                                enter-to-class="transform scale-100 opacity-100"
                                leave-active-class="transition duration-75 ease-out"
                                leave-from-class="transform scale-100 opacity-100"
                                leave-to-class="transform scale-95 opacity-0"
                            >
                                <DisclosurePanel class="text-gray-500">
                                    <SwitchGroup
                                        as="div"
                                        class="flex items-start justify-between gap-4 px-2 pb-4 mt-2"
                                    >
                                        <span class="flex flex-col flex-grow">
                                            <SwitchDescription
                                                as="span"
                                                class="text-xs text-gray-300 md:text-sm"
                                            >{{ item.description }}</SwitchDescription>
                                        </span>
                                        <Switch
                                            v-if="item.showToggle"
                                            v-model="item.allowed"
                                            :class="[
                                                item.allowed
                                                    ? 'bg-purple-500'
                                                    : 'bg-gray-500',
                                                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200',
                                                'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                                            ]"
                                        >
                                            <span
                                                :class="[
                                                    item.allowed
                                                        ? 'translate-x-5'
                                                        : 'translate-x-0',
                                                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                                                ]"
                                                aria-hidden="true"
                                            />
                                        </Switch>
                                    </SwitchGroup>
                                </DisclosurePanel>
                            </transition>
                        </Disclosure>
                    </div>
                </div>
                <div class="flex flex-col justify-end w-full gap-4 pt-5 border-t border-gray-600 md:flex-row md:gap-8">
                    <BaseButton
                        class="text-black "
                        to="/privacy-policy"
                        variant="text"
                        @click="showPrivacyPolicyClicked"
                    >
                        Privacy Policy
                    </BaseButton>
                    <BaseButton
                        class="text-black "
                        variant="text"
                        @click="acceptChoicesClicked"
                    >
                        Confirm my choices
                    </BaseButton>
                    <BaseButton
                        class=""
                        @click="acceptAllClicked"
                    >
                        Accept All
                    </BaseButton>
                </div>
            </div>
        </div>

        <div
            v-if="!showBanner"
            :class="acceptedAllCookies ? 'opacity-30 bottom-2 left-2' : 'bg-purple-500 bottom-2 md:bottom-10 left-2 md:left-10'"
            class="fixed transition-all rounded"
        >
            <BaseButton
                :class="acceptedAllCookies ? 'px-1' : 'px-2 py-1'"
                variant="text"
                @click="showSettingsClicked"
            >
                <ShieldCheckIcon
                    aria-hidden="true"
                    class="inline-block w-5 h-5 text-white"
                />
            </BaseButton>
        </div>
    </div>
</template>

<script lang="ts" setup>
import BaseButton from "../../common/components/BaseButton.vue";
import { computed, onMounted, reactive, ref } from "vue";
import { ShieldCheckIcon, XIcon } from "@heroicons/vue/outline";
import { Disclosure, DisclosureButton, DisclosurePanel, Switch, SwitchDescription, SwitchGroup } from "@headlessui/vue";
import { ChevronRightIcon } from "@heroicons/vue/solid";
import { loadCookieSettings, saveCookieSettings } from "../services/cookieConsent.js";
import * as Analytics from "./../../app/services/analytics";

const showSettings = ref(null);

/**
 * Reactive property to keep track of the current cookie settings.
 * Should be refreshed with the latest settings in order to correctly
 * render the changes.
 */
const currentSettings = ref(null);

/**
 * Reactive list of the displayed settings.
 */
const cookieSettingsData = reactive([
    {
        id: 0,
        title: "Necessary Cookies",
        description: "These cookies are necessary for the website to function and cannot be switched off. These cookies do not store any personally identifiable information.",
        showToggle: false,
        allowed: true
    },
    {
        id: 1,
        title: "Performance",
        description: "These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. All information these cookies collect is aggregated and therefore anonymous.",
        showToggle: true,
        allowed: true
    },
    {
        id: 2,
        title: "Functional",
        description: "These cookies allow us to remember the settings you changed on our website or choices you made to give you better functionality and personal features.",
        showToggle: true,
        allowed: true
    },
    {
        id: 3,
        title: "Targeting",
        description: "These cookies are used to provide you with relevant ads and marketing campaigns. They do not store directly personal information, but are based on uniquely identifying your browser or internet device. They may be set through our site by our partners and can be used by those partners on other sites. Some third-party video providers may not work without targeting cookies.",
        showToggle: true,
        allowed: false
    }
]);

onMounted(() =>
{
    refreshSettings();
});

/**
 * Computes if the cookie consent banner should show.
 * The banner should show if no cookie consent settings are stored.
 */
const showBanner = computed(() =>
{
    return !currentSettings.value;
});

/**
 * Computes if the user accepted all the cookies.
 * @returns true if the user accepted all cookies,
 * false if the user only accepted some of the cookies.
 */
const acceptedAllCookies = computed(() =>
{
    return currentSettings.value
        && currentSettings.value.performance
        && currentSettings.value.functional
        && currentSettings.value.targeting;
});

/**
 * Loads the current cookie settings and applies the settings to
 * the elements.
 */
function refreshSettings ()
{
    currentSettings.value = loadCookieSettings();
    if (currentSettings.value)
    {
        cookieSettingsData[1].allowed = currentSettings.value.performance;
        cookieSettingsData[2].allowed = currentSettings.value.functional;
        cookieSettingsData[3].allowed = currentSettings.value.targeting;
    }
}

function acceptAllClicked ()
{
    saveCookieSettings(true, true, true, true);
    refreshSettings();
    Analytics.setConsent({
        performance: true,
        targeting: true
    });
    showSettings.value = false;
}

function acceptChoicesClicked ()
{
    saveCookieSettings(
        true,
        cookieSettingsData[1].allowed,
        cookieSettingsData[2].allowed,
        cookieSettingsData[3].allowed);

    refreshSettings();

    Analytics.setConsent({
        performance: cookieSettingsData[1].allowed,
        targeting: cookieSettingsData[3].allowed
    });

    showSettings.value = false;
}

function showSettingsClicked ()
{
    refreshSettings();
    showSettings.value = true;
}

function showPrivacyPolicyClicked ()
{
    showSettings.value = false;
}

function closeSettingsClicked ()
{
    showSettings.value = false;
}

</script>
